import { v4 as uuidv4 } from 'uuid';

const NOTIFICATION_OPEN_TOKEN_PARAM = 'notificationOpenToken';
const NOTIFICATION_OPEN_MAX_AGE_MS = 30_000;
const pendingNotificationOpens = new Map<string, { route: string; expiresAt: number }>();

const routeForUrl = (url: URL): string => `${url.pathname}${url.search}`;

const isOpenableReportUrl = (url: URL, origin: string): boolean =>
  url.origin === origin &&
  (url.pathname === '/reports' || url.pathname.startsWith('/reports/')) &&
  Boolean(url.searchParams.get('parentConfigId'));

/**
 * Issues a one-use marker for a report link clicked from a notification.
 * A copied or ordinary deep link cannot claim notification attribution by
 * adding a query parameter on its own.
 */
export const markReportNotificationPath = (path: string): string => {
  try {
    const origin = typeof window === 'undefined' ? 'https://simplifi.local' : window.location.origin;
    const url = new URL(path, origin);
    if (!isOpenableReportUrl(url, origin)) return path;

    for (const [token, pending] of pendingNotificationOpens) {
      if (pending.expiresAt < Date.now()) pendingNotificationOpens.delete(token);
    }

    const token = uuidv4();
    url.searchParams.set(NOTIFICATION_OPEN_TOKEN_PARAM, token);
    const route = routeForUrl(url);
    pendingNotificationOpens.set(token, { route, expiresAt: Date.now() + NOTIFICATION_OPEN_MAX_AGE_MS });
    return `${route}${url.hash}`;
  } catch {
    return path;
  }
};

/** Consumes the click marker before opening, so retries and reloads cannot emit another event. */
export const consumeReportNotificationPath = (path: string): boolean => {
  try {
    const url = new URL(path, 'https://simplifi.local');
    const token = url.searchParams.get(NOTIFICATION_OPEN_TOKEN_PARAM);
    if (!token) return false;

    const pending = pendingNotificationOpens.get(token);
    pendingNotificationOpens.delete(token);
    return Boolean(pending && pending.route === routeForUrl(url) && pending.expiresAt >= Date.now());
  } catch {
    return false;
  }
};
